import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import type { AxiosError } from 'axios'
import axios, { isAxiosError } from 'axios'

import { ValidationErrors } from 'store/types'

import { getAxiosInstance } from 'services/api'

import { Tournament } from 'types/app'

import {
  JoinTournamentParams,
  MatchResultResponse,
  SwitchTeamInMatchParams,
  TeamScoreModalParamsRequired,
  TeamScoreRequestParams,
  MatchWinnerModalParamsRequired,
  MatchWinnerRequestParams,
  LeaveTournamentParams,
} from './types'
import {
  buildCreateTournamentFormData,
  type CreateTournamentParams,
} from './createTournamentFormData'

const api = getAxiosInstance()

export const getAllTournaments = createAsyncThunk<
  Tournament[],
  string, // Change the type of the payload to string
  { rejectValue: ValidationErrors }
>(
  'tournament/getAllTournaments',
  // status: 'end' | 'ongoing' | 'upcoming'
  async (status, { rejectWithValue }) => {
    // Adjust the arguments to accept only 'status'
    try {
      const response = await api.get(`/all/tournaments?status=${status}`)
      return response.data.tournaments
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

// Get all tournaments for a specific community
export const getCommunityTournaments = createAsyncThunk<
  Tournament[],
  string, // communityId
  { rejectValue: ValidationErrors }
>(
  'tournament/getCommunityTournaments',
  async (communityId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/all/tournaments?communityId=${communityId}`)
      return response.data.tournaments
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

export const getTournamentById = createAsyncThunk<
  Tournament,
  string,
  { rejectValue: ValidationErrors }
>('tournament/getTournamentById', async (tournamentId, { rejectWithValue }) => {
  try {
    const response = await api.get(`/tournament/details/${tournamentId}`)
    return response.data.tournament
  } catch (err) {
    const error = err as AxiosError<ValidationErrors>

    if (!error.response) throw err

    return rejectWithValue(error.response.data)
  }
})

export const createTournament = createAsyncThunk<
  Tournament,
  CreateTournamentParams,
  { rejectValue: ValidationErrors }
>(
  'tournament/create',
  async (tournamentParams, { rejectWithValue }) => {
    try {
      const params = buildCreateTournamentFormData(tournamentParams)

      const response = await api.post('/admin/create/tournament', params, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      })
      return response.data.tournament
    } catch (err) {
      const error = err as AxiosError<ValidationErrors>

      if (!error.response) throw err

      return rejectWithValue(error.response.data)
    }
  },
)

export const joinTournament = createAsyncThunk(
  'tournament/joinTournament',
  async (
    { tournamentId, teamId, selectedPlayers }: JoinTournamentParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(
        `tournament/join/${tournamentId}`,
        { teamId, selectedPlayers }
      )
      // const response = await api.get('/all/tournaments')
      // return response.data
      return {
        success: response.data.success,
        updatedTournament: response.data.tournament,
      }
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

export const leaveTournament = createAsyncThunk(
  'tournament/leaveTournament',
  async (
    { tournamentId, teamId }: LeaveTournamentParams,
    { rejectWithValue },
  ) => {
    try {
      const response = await api.delete(`/tournament/leave/${tournamentId}`, {
        data: { teamId },
      })

      // const allTournamentsResponse = await api.get('/all/tournaments')
      // return allTournamentsResponse.data.tournaments

      // return {
      //   tournaments: allTournamentsResponse.data.tournaments,
      //   success: response.data.success,
      //   updatedTournament: response.data.tournament,
      // }
      return response.data
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

export const fetchBracketData = createAsyncThunk(
  'tournament/fetchBracketData',
  async (tournamentId: string, { rejectWithValue }) => {
    try {
      const apiUrl = `${
        import.meta.env.VITE_API_BASE_URL
      }/tournament/${tournamentId}/bracketData`
      const response = await axios.get(apiUrl)

      if (!response.data.success) {
        console.error('Invalid response format', response.data)
        return rejectWithValue('Invalid response format')
      }

      return { tournamentId, bracketData: response.data.bracketData }
    } catch (error) {
      if (isAxiosError(error)) {
        console.error('Failed to fetch bracket data:', error.response?.data)
        return rejectWithValue(error.response?.data)
      }
      console.error('Unknown error:', error)
      return rejectWithValue('Unknown error occurred')
    }
  },
)

export const submitMatchResult = createAsyncThunk<
  MatchResultResponse,
  { tournamentId: string; matchId: string; winningTeamId: string }
>(
  'tournament/submitMatchResult',
  async ({ tournamentId, matchId, winningTeamId }, { rejectWithValue }) => {
    const apiUrl = `${
      import.meta.env.VITE_API_BASE_URL
    }/tournament/${tournamentId}/match-result`
    const payload = { tournamentId, matchId, winningTeamId }

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json()
        return rejectWithValue(errorData)
      }

      const responseData: MatchResultResponse = await response.json()
      if (responseData.success) {
        return responseData
      } else {
        return rejectWithValue(responseData.message)
      }
    } catch (error) {
      console.error('Error submitting match result:', error)
      return rejectWithValue(error)
    }
  },
)

export const switchTeamInMatch = createAsyncThunk(
  'tournament/SwitchTeamInMatch',
  async (
    {
      tournamentId,
      roundIndex,
      matchIndex,
      oldTeamId,
      newTeamId,
      bracketType,
    }: SwitchTeamInMatchParams,
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await api.post(
        `/tournament/${tournamentId}/switchteam`,
        { roundIndex, matchIndex, oldTeamId, newTeamId, bracketType }
      )

      const result = {
        tournamentId,
        success: response.data.success,
        updatedBracket: response.data.tournament,
      }

      // Background refetch for full data consistency
      dispatch(getTournamentById(tournamentId))

      return result
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

export const teamScoreModalOpen = createAction(
  'tournament [TeamScoreModal] Open',
  (payload: TeamScoreModalParamsRequired) => ({ payload }),
)

export const teamScoreModalClose = createAction(
  'tournament [TeamScoreModal] Close',
)

export const setTeamScore = createAsyncThunk(
  'tournament/SetTeamScore',
  async (
    {
      tournamentId,
      roundIndex,
      matchIndex,
      teamId,
      score,
      opponentScore,
      screenshots,
      bracketType,
    }: TeamScoreRequestParams,
    { rejectWithValue },
  ) => {
    try {
      const data = new FormData()
      data.append('roundIndex', `${roundIndex}`)
      data.append('matchIndex', `${matchIndex}`)
      data.append('score', `${score}`)
      data.append('teamId', `${teamId}`)
      if (opponentScore !== undefined) {
        data.append('opponentScore', `${opponentScore}`)
      }
      if (bracketType) {
        data.append('bracketType', bracketType)
      }
      if (screenshots) {
        for (let index = 0; index < screenshots.length; index++) {
          data.append('screenshots', screenshots[index])
        }
      }
      const response = await api.post(`/tournament/${tournamentId}/teamscore`, data, {
        headers: {
          'content-type': 'multipart/form-data',
        },
      })

      // Fetch updated tournament to reflect score changes
      const tournament = await api.get(`/tournament/details/${tournamentId}`)

      return {
        success: true,
        updatedTournament: tournament.data.tournament,
        message: response.data.message,
      }
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

export const matchWinnerModalOpen = createAction(
  'tournament [MatchWinnerModal] Open',
  (payload: MatchWinnerModalParamsRequired) => ({ payload }),
)

export const matchWinnerModalClose = createAction(
  'tournament [MatchWinnerModal] Close',
)

export const setMatchWinner = createAsyncThunk(
  'tournament/SetMatchWinner',
  async (
    {
      tournamentId,
      roundIndex,
      matchIndex,
      participants,
      bracketType,
    }: MatchWinnerRequestParams,
    { rejectWithValue },
  ) => {
    try {
      const data = {
        roundIndex,
        matchIndex,
        participants,
        bracketType,
      }
      await api.post(`/tournament/${tournamentId}/matchwinner`, data)

      // const allTournamentsResponse = await api.get('/all/tournaments')
      const tournament = await api.get(`/tournament/details/${tournamentId}`)

      return {
        // tournaments: allTournamentsResponse.data.tournaments,
        success: true,
        updatedTournament: tournament.data.tournament,
      }
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)
// /admin/change/tournament/status/:id
export const updateTournamentStatus = createAsyncThunk(
  'tournament/updateTournamentStatus',
  async (
    {
      tournamentId,
      status,
    }: { tournamentId: string; status: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.put(
        `/admin/change/tournament/status/${tournamentId}`,
        { status }
      )

      // const allTournamentsResponse = await api.get('/all/tournaments')

      return {
        success: response.data.success,
        updatedTournament: response.data.tournament,
        tournament: response.data.tournament, // Include tournament for status check
        message: response.data.message,
        // Draft cups (DRAFT_MODE_PLAN §7): branch on draftStatus, never on message.
        draftStatus: (response.data.draftStatus ??
          response.data.tournament?.draft_settings?.draft_status) as string | undefined,
        captainsNeeded: response.data.captainsNeeded,
        teamCount: response.data.teamCount,
        subPoolSize: response.data.subPoolSize,
        playersForDraft: response.data.playersForDraft,
        totalPlayers: response.data.totalPlayers,
        playersForSubPool: response.data.playersForSubPool,
      }
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

// Start Group Play - For tournaments with groupPlayEnabled (LEGACY - does everything in one step)
export const startGroupPlay = createAsyncThunk(
  'tournament/startGroupPlay',
  async (
    {
      tournamentId,
      config,
    }: { tournamentId: string; config?: { teamsPerGroup?: number; playInEnabled?: boolean } },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(
        `/tournament/${tournamentId}/group-play/start`,
        { config }
      )

      return {
        success: response.data.success,
        message: response.data.message,
        updatedTournament: response.data.tournament || response.data,
        structure: response.data.structure,
        groups: response.data.groups,
      }
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

// Generate Groups - Step 1: Creates groups for admin review (phase → group_assignment)
export const generateGroups = createAsyncThunk(
  'tournament/generateGroups',
  async (
    {
      tournamentId,
      config,
    }: { tournamentId: string; config?: { teamsPerGroup?: number; playInEnabled?: boolean } },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(
        `/tournament/${tournamentId}/group-play/generate-groups`,
        { config }
      )

      return {
        success: response.data.success,
        message: response.data.message,
        tournament: response.data.tournament,
        structure: response.data.structure,
        groups: response.data.groups,
      }
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

// Start Group Matches - Step 2: Starts matches after admin review (phase → group_play)
export const startGroupMatches = createAsyncThunk(
  'tournament/startGroupMatches',
  async (tournamentId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `/tournament/${tournamentId}/group-play/start-group-matches`
      )

      return {
        success: response.data.success,
        message: response.data.message,
        tournament: response.data.tournament,
        groups: response.data.groups,
        totalMatches: response.data.totalMatches,
      }
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

// Swap Teams Between Groups - For admin review phase
export const swapTeamsBetweenGroups = createAsyncThunk(
  'tournament/swapTeams',
  async (
    {
      tournamentId,
      team1Id,
      team2Id,
    }: { tournamentId: string; team1Id: string; team2Id: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(
        `/tournament/${tournamentId}/groups/swap-teams`,
        { team1Id, team2Id }
      )

      return {
        success: response.data.success,
        message: response.data.message,
        groups: response.data.groups,
      }
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

// Move Team to Group - For admin review phase (move without swap)
export const moveTeamToGroup = createAsyncThunk(
  'tournament/moveTeam',
  async (
    {
      tournamentId,
      teamId,
      targetGroupName,
    }: { tournamentId: string; teamId: string; targetGroupName: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(
        `/tournament/${tournamentId}/groups/assign-team`,
        { teamId, targetGroupName }
      )
      return {
        success: response.data.success,
        message: response.data.message,
        groups: response.data.groups,
      }
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

export const deleteTournament = createAsyncThunk(
  'tournament/deleteTournament',
  async (tournamentId: string, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/admin/tournament/${tournamentId}`, {
        // headers: { Authorization: `Bearer ${token}` },
      })

      // const allTournamentsResponse = await api.get('/all/tournaments')

      return {
        success: response.data.success,
        message: response.data.message,
        tournamentId,
        // updatedTournament: response.data.tournament,
      }
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

export const retryTournamentDeletion = createAsyncThunk(
  'tournament/retryTournamentDeletion',
  async (tournamentId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/tournament/${tournamentId}/retry-deletion`)

      return {
        success: response.data.success,
        message: response.data.message,
        tournamentId,
        data: response.data.data,
      }
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

// Sub request management actions
export const getPendingSubRequests = createAsyncThunk(
  'tournament/getPendingSubRequests',
  async (tournamentId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/tournament/draft/pending-subs/${tournamentId}`)
      return response.data
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

export const approveSubRequest = createAsyncThunk(
  'tournament/approveSubRequest',
  async ({ tournamentId, requestId, substitutePlayerId }: { tournamentId: string; requestId: string; substitutePlayerId?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/tournament/draft/approve-sub/${tournamentId}`, {
        requestId,
        substitutePlayerId,
      })
      return response.data
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

export const denySubRequest = createAsyncThunk(
  'tournament/denySubRequest',
  async ({ tournamentId, requestId, denialReason }: { tournamentId: string; requestId: string; denialReason?: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/tournament/draft/deny-sub/${tournamentId}`, {
        requestId,
        denialReason,
      })
      return response.data
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

export const requestSubFromPool = createAsyncThunk(
  'tournament/requestSubFromPool',
  async ({ tournamentId, teamIndex, replacingPlayerId, reason }: {
    tournamentId: string;
    teamIndex: number;
    replacingPlayerId: string;
    reason?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/tournament/draft/request-sub/${tournamentId}`, {
        teamIndex,
        replacingPlayerId,
        reason,
      })
      return response.data
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)

export const adminRemovePlayerFromDraft = createAsyncThunk(
  'tournament/adminRemovePlayerFromDraft',
  async (
    { tournamentId, playerId, removeFromTournament = false }: { tournamentId: string; playerId: string; removeFromTournament?: boolean },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.post(`/admin/tournament/draft/remove-player/${tournamentId}`, {
        playerId,
        ...(removeFromTournament ? { removeFromTournament: true } : {}),
      })
      return response.data
    } catch (error) {
      if (isAxiosError(error)) {
        return rejectWithValue(error.response?.data)
      }
      throw error
    }
  },
)
